// Data-driven chart colors for the home dashboard, defined once.
//
// SVG attributes (recharts stroke/fill) and the heatmap cell fills cannot take
// Tailwind classes, so they get raw values from here. Everything else on the
// dashboard (bars, text, surfaces) uses Tailwind tokens with `dark:` variants.
// The values mirror Tailwind's blue / gray / amp-* tokens so the charts match
// the surrounding classes in both themes (COCKPIT.md 2.9 point 1 exception for
// genuinely data-driven colors).

import { useEffect, useState } from 'react';

export type ChartPalette = {
  /** This year (accent blue). */
  accent: string;
  /** Last year (muted gray). */
  muted: string;
  /** Gridlines (amp-line / amp-dark-line). */
  grid: string;
  /** Axis text (amp-ink-muted / amp-dark-ink-muted). */
  axis: string;
  /** Seven-step sequential blue scale, light = slow, dark = busy. */
  heat: readonly string[];
};

export const LIGHT_PALETTE: ChartPalette = {
  accent: '#2563EB',
  muted: '#9CA3AF',
  grid: '#E2D7B8',
  axis: '#2E2719',
  heat: ['#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB', '#1E3A8A'],
};

// On a dark surface "busy" reads as the brightest step, so the scale runs from
// a dim navy (slow) up to a bright blue (busy).
export const DARK_PALETTE: ChartPalette = {
  accent: '#60A5FA',
  muted: '#6B7280',
  grid: '#3D3833',
  axis: '#C6B98E',
  heat: ['#1E293B', '#1E3A5F', '#1D4ED8', '#2563EB', '#3B82F6', '#60A5FA', '#BFDBFE'],
};

function readDark(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

/** Tracks the `dark` class on <html> (set by useTheme) and returns the matching palette. */
export function useChartPalette(): ChartPalette {
  const [dark, setDark] = useState(readDark);
  useEffect(() => {
    setDark(readDark());
    const obs = new MutationObserver(() => setDark(readDark()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark ? DARK_PALETTE : LIGHT_PALETTE;
}
